import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { BlogCategoriesAPI } from '@/api';

import { StyledContentWrapper, StyledTableWrapper, StyledTabTitleWrapper } from '../../components/Styled';
import { Button, Drawer, Table } from '@/ui';
import { PlusCircleIcon } from '@/icons';
import { showSuccessToast, useForm } from '@/components';
import DeleteModal from '../../components/DeleteModal/DeleteModal';
import BlogCategoryForm from './BlogCategoryForm';

import { IBlogCategory, IBlogCategorySaveDTO } from '@/models/blogCategories.model';
import { CategoryLocaleCodes } from '@/models/categories.model';

import { useModal } from '@/hooks';
import { Resource, usePermissions } from '@/modules/Permissions';
import { getBlogCategoriesTableColumns } from './columns';
import { formatApiError, showApiErrors } from '@/utils';

const BLOG_CATEGORY_EMPTY_FORM_VALUES = {
  position: 0,
  locale: {
    [CategoryLocaleCodes.UK]: { name: '' },
    [CategoryLocaleCodes.RU]: { name: '' },
  },
};

const BlogCategoriesTabContainer = () => {
  const { t } = useTranslation(['categories']);
  const { canManage } = usePermissions();
  const [form] = useForm();
  const [list, setList] = useState<IBlogCategory[]>([]);
  const [isLoadingList, setIsLoadingList] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const { isOpened: isDrawerOpened, openModal: openDrawer, closeModal: closeDrawer } = useModal();
  const { isOpened: isDeleteModalOpen, openModal: openDeleteModal, closeModal: closeDeleteModalBase } = useModal();

  const [editingCategory, setEditingCategory] = useState<IBlogCategory | null>(null);
  const [deletingCategory, setDeletingCategory] = useState<IBlogCategory | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const closeDeleteModal = useCallback(() => {
    setDeletingCategory(null);
    closeDeleteModalBase();
  }, [closeDeleteModalBase]);

  const loadList = useCallback(async () => {
    setIsLoadingList(true);
    try {
      const { data } = await BlogCategoriesAPI.getList();
      const rows = data?.data ?? [];
      setList([...rows].sort((a, b) => a.position - b.position));
    } catch (e) {
      showApiErrors(formatApiError(e));
    } finally {
      setIsLoadingList(false);
    }
  }, []);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const drawerInitialValues = useMemo(() => {
    if (editingCategory) {
      return {
        position: editingCategory.position,
        locale: {
          [CategoryLocaleCodes.UK]: {
            name: editingCategory.locale?.[CategoryLocaleCodes.UK]?.name ?? '',
          },
          [CategoryLocaleCodes.RU]: {
            name: editingCategory.locale?.[CategoryLocaleCodes.RU]?.name ?? '',
          },
        },
      };
    }
    return BLOG_CATEGORY_EMPTY_FORM_VALUES;
  }, [editingCategory]);

  useLayoutEffect(() => {
    if (!isDrawerOpened) return;
    if (editingCategory) {
      form.setFieldsValue({
        position: editingCategory.position,
        locale: {
          [CategoryLocaleCodes.UK]: {
            name: editingCategory.locale?.[CategoryLocaleCodes.UK]?.name ?? '',
          },
          [CategoryLocaleCodes.RU]: {
            name: editingCategory.locale?.[CategoryLocaleCodes.RU]?.name ?? '',
          },
        },
      });
    } else {
      form.setFieldsValue(BLOG_CATEGORY_EMPTY_FORM_VALUES);
    }
  }, [isDrawerOpened, editingCategory?.id, form]);

  const onDrawerClose = async () => {
    await form.resetFields();
    setEditingCategory(null);
    closeDrawer();
  };

  const onCreate = () => {
    setEditingCategory(null);
    openDrawer();
  };

  const handleEditClick = (row: IBlogCategory) => {
    setEditingCategory(row);
    openDrawer();
  };

  const handleDeleteClick = (row: IBlogCategory) => {
    setDeletingCategory(row);
    openDeleteModal();
  };

  const handleFormSubmit = async (values: IBlogCategorySaveDTO) => {
    setSubmitLoading(true);
    try {
      if (editingCategory) {
        await BlogCategoriesAPI.update(editingCategory.id, values);
        showSuccessToast({ message: t('blog_category_edit_success') });
      } else {
        await BlogCategoriesAPI.create(values);
        showSuccessToast({ message: t('blog_category_create_success') });
      }
      await loadList();
      await onDrawerClose();
    } catch (e) {
      showApiErrors(formatApiError(e));
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deletingCategory) return;
    setDeleteLoading(true);
    try {
      const { data } = await BlogCategoriesAPI.remove(deletingCategory.id);
      if (data?.data?.success) {
        showSuccessToast({ message: t('blog_category_delete_success') });
        setDeletingCategory(null);
        closeDeleteModal();
        await loadList();
      }
    } catch (e) {
      showApiErrors(formatApiError(e));
    } finally {
      setDeleteLoading(false);
    }
  };

  const deleteTitleName =
    deletingCategory?.locale?.[CategoryLocaleCodes.UK]?.name ||
    deletingCategory?.locale?.[CategoryLocaleCodes.RU]?.name ||
    '';

  return (
    <>
      <StyledTabTitleWrapper style={{ justifyContent: 'flex-end' }}>
        <Button onClick={onCreate} icon={<PlusCircleIcon style={{ maxWidth: '2rem', fill: '#000' }} />}>
          {t('blog_category_new_button')}
        </Button>
      </StyledTabTitleWrapper>

      <StyledContentWrapper>
        <StyledTableWrapper>
          <Table
            rowKey="id"
            loading={isLoadingList}
            columns={getBlogCategoriesTableColumns({
              t,
              onEdit: handleEditClick,
              onDelete: handleDeleteClick,
              canManage: canManage(Resource.CONTENT),
            })}
            dataSource={list}
            pagination={false}
          />
        </StyledTableWrapper>
      </StyledContentWrapper>

      <Drawer open={isDrawerOpened} onClose={onDrawerClose}>
        {isDrawerOpened && (
          <BlogCategoryForm
            key={editingCategory?.id ?? 'new'}
            form={form}
            modeKey={editingCategory?.id ?? 'new'}
            isEdit={!!editingCategory}
            initialValues={drawerInitialValues}
            onSubmit={handleFormSubmit}
            submitLoading={submitLoading}
          />
        )}
      </Drawer>

      <DeleteModal
        isOpen={isDeleteModalOpen}
        isLoading={deleteLoading}
        onClose={closeDeleteModal}
        onConfirm={handleDeleteConfirm}
        title={t('blog_category_delete_title', { name: deleteTitleName })}
        closeBtnText={t('delete_category_back')}
        confirmBtnText={t('blog_category_delete_confirm')}
      />
    </>
  );
};

export default BlogCategoriesTabContainer;
